import { Injectable, OnDestroy, inject } from '@angular/core';

import { ConfigStateService } from '@abp/ng.core';
import { BehaviorSubject, EMPTY, Observable, Subscription, interval, of, timer } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { SammDataAccessFacade } from '@nexweb/shared/data-access/samm';
import { DateTimeHelperService } from '@nexweb/shared/date-time-helper';
import { isNullOrUndefined } from '@nexweb/util';

@Injectable({
  providedIn: 'root',
})
export class SharedUiComponentService implements OnDestroy {
  private sammDataAccessFacade = inject(SammDataAccessFacade);
  private configStateService = inject(ConfigStateService);
  private dateTimeHelperService = inject(DateTimeHelperService);

  private serverResync: number;
  private _serverTime$ = new BehaviorSubject<Date>(undefined);
  private _serverTimeDisplay$ = new BehaviorSubject<string>(null);
  private _serverTimeInterval$: Subscription;
  private _serverTimeResyncTimer$: Subscription;
  private _serverTimeOffsetMs = 0;
  private _use24hClock = false;

  initialise() {
    this.serverResync = Number(this.configStateService.getSetting('NexBase.SyncServerDateTime'));
    this.getServerTime();
  }

  getServerTime$(): Observable<Date> {
    return this._serverTime$.asObservable();
  }

  getServerTimeDisplay$(): Observable<string> {
    return this._serverTimeDisplay$.asObservable();
  }

  getServerTime() {
    this._serverTimeInterval$?.unsubscribe();
    this._serverTimeResyncTimer$?.unsubscribe();

    this._serverTimeResyncTimer$ = timer(0, this.serverResync * 1000)
      .pipe(
        switchMap(() => {
          if (this._getCurrentUser()) {
            return this.sammDataAccessFacade.getServerTime().pipe(
              map((result) => {
                if (result) {
                  this._serverTimeOffsetMs = result.getTime() - Date.now();
                  this._publishServerTime(result);
                }
              }),
            );
          } else {
            return of(0);
          }
        }),
        catchError(() => of(EMPTY)),
      )
      .subscribe();

    // Sub-second polling keeps the displayed second from repeating or skipping when a tick runs late.
    this._serverTimeInterval$ = interval(250)
      .pipe(
        map(() => {
          if (this._serverTime$.value !== undefined) {
            this._publishServerTime(new Date(Date.now() + this._serverTimeOffsetMs));
          }
        }),
      )
      .subscribe();
  }

  setUse24hClock(value: boolean): void {
    this._use24hClock = value;
    if (!isNullOrUndefined(this._serverTime$?.value)) {
      this._serverTimeDisplay$.next(this._formatForDisplay(this._serverTime$.value));
    }
  }

  format() {
    return this.dateTimeHelperService.fullDateTimeFormat;
  }

  ngOnDestroy(): void {
    this._serverTimeInterval$?.unsubscribe();
    this._serverTimeResyncTimer$?.unsubscribe();
    this._serverTimeDisplay$?.unsubscribe();
    this._serverTime$?.unsubscribe();
  }

  private _publishServerTime(time: Date): void {
    const display = this._formatForDisplay(time);

    if (display === this._serverTimeDisplay$.value) {
      return;
    }

    this._serverTime$.next(time);
    this._serverTimeDisplay$.next(display);
  }

  private _formatForDisplay(date: Date): string {
    if (!this._use24hClock) {
      return this.dateTimeHelperService.formatFullDateTime(date);
    }
    // Assumes NexBase.FullDateFormat follows date-fns conventions: `h`/`hh` are the only
    // lowercase-h tokens, and unquoted `a` is exclusively the AM/PM designator.
    const fmt = this.dateTimeHelperService.fullDateTimeFormat
      .replace(/(?<![a-zA-Z])hh(?![a-zA-Z])/g, 'HH')
      .replace(/(?<![a-zA-Z])h(?![a-zA-Z])/g, 'H')
      .replace(/(?<![a-zA-Z'])\s*a+\s*(?![a-zA-Z'])/g, '')
      .trim();

    return this.dateTimeHelperService.format(date, fmt);
  }

  private _getCurrentUser(): boolean {
    return this.configStateService.getDeep('currentUser.isAuthenticated') as boolean;
  }
}
